// ============================================================================
// PRICING — public page (/pricing)
// Razorpay requires a publicly visible price before activating a subscription
// business. Values are static and come from companyInfo.pricing — keep them in
// sync with the billing_config and duration_options tables in the database.
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import LegalLayout from './LegalLayout';
import companyInfo from './companyInfo';

const { seatMonthlyPrice, currency, currencySymbol, durations } = companyInfo.pricing;

const money = (n) => `${currencySymbol}${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Mirrors the server-side seat pricing: base rate less the duration discount,
// multiplied by the number of months in the cycle.
const perSeatFor = (months, discountPct) =>
  Math.round(seatMonthlyPrice * (1 - discountPct / 100) * months * 100) / 100;

const INCLUDED = [
  'Lead capture, assignment and status tracking',
  'Follow-up scheduling with reminders and missed-followup views',
  'Role-based access with granular permissions',
  'Team hierarchy and user management',
  'Attendance tracking and policies',
  'Projects, properties and broker records',
  'Reports and dashboards',
  'Push and email notifications',
  'Dedicated database for your organisation',
];

const Pricing = () => (
  <LegalLayout
    title="Pricing"
    subtitle="One shared billing cycle. Buy seats in bulk, add users any time."
    showEffectiveDate={false}
  >
    <section>
      <div className="legal__price-hero">
        <div className="legal__price-amount">
          {money(seatMonthlyPrice)}
          <span>/ seat / month</span>
        </div>
        <p className="legal__price-note">
          Billed in {currency}. Exclusive of applicable taxes.
        </p>
      </div>
    </section>

    <section>
      <h2>Cycle lengths</h2>
      <div className="legal__table-wrap">
        <table className="legal__table">
          <thead>
            <tr>
              <th>Cycle</th>
              <th>Discount</th>
              <th>Per seat</th>
              <th>Effective monthly</th>
            </tr>
          </thead>
          <tbody>
            {durations.map((d) => (
              <tr key={d.months}>
                <td>{d.label}</td>
                <td>{d.discountPct > 0 ? `${d.discountPct}% off` : '—'}</td>
                <td>{money(perSeatFor(d.months, d.discountPct))}</td>
                <td>{money(perSeatFor(d.months, d.discountPct) / d.months)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="legal__price-note">
        Total payable = per-seat price × number of seats selected.
      </p>
    </section>

    <section>
      <h2>What is included</h2>
      <ul className="legal__check-list">
        {INCLUDED.map((item) => (
          <li key={item}><Check size={15} /> {item}</li>
        ))}
      </ul>
      <p className="legal__placeholder">
        [ADJUST THIS FEATURE LIST, AND ADD ANY PLAN TIERS OR ADD-ONS YOU SELL]
      </p>
    </section>

    <section>
      <h2>How billing works</h2>
      <h3>One shared cycle</h3>
      <p>
        All seats on your account expire on the same date. This keeps one renewal to manage
        instead of a separate date per user.
      </p>

      <h3>Adding a user mid-cycle</h3>
      <p>
        A user added part-way through a cycle pays only for the days remaining, charged at
        the seat&rsquo;s daily rate for that cycle. The new seat lands on the same renewal
        date as the rest of the account.
      </p>

      <h3>Bringing a lapsed seat back</h3>
      <p>
        If a seat expired and is renewed later, a catch-up charge covers the unpaid days so
        that seat realigns to the shared renewal date.
      </p>

      <h3>Renewal</h3>
      <p>
        Renewal is <strong>manual</strong>. We do not store your payment instrument and there
        is no auto-debit. An administrator completes the payment before the cycle ends.
      </p>

      <h3>Locked rate</h3>
      <p>
        The per-seat rate is locked when a cycle starts. If published prices change later, it
        does not affect a cycle you have already paid for.
      </p>
    </section>

    <section>
      <h2>Payment methods</h2>
      <p>
        Payments are accepted via UPI, credit and debit cards, and net banking, processed
        through our payment gateway partner. We never see or store your card, UPI or net
        banking credentials.
      </p>
    </section>

    <section>
      <h2>Taxes and invoices</h2>
      <p>
        Prices shown are exclusive of GST. Tax is applied at the rate in force on the date of
        invoice. A GST invoice is issued for every payment against the billing details on
        your account.
      </p>
    </section>

    <section>
      <h2>Cancellation</h2>
      <p>
        Since nothing auto-renews, you cancel by not renewing. Full details are on our{' '}
        <Link to="/refund">Refund and Cancellation</Link> page.
      </p>
    </section>

    <section>
      <h2>Questions</h2>
      <p>
        For custom seat volumes or a quote, contact {companyInfo.supportEmail} or see the{' '}
        <Link to="/contact">Contact</Link> page.
      </p>
    </section>
  </LegalLayout>
);

export default Pricing;
